/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NavigationSection } from '@squiz/sds';
import { create } from 'zustand';
import { DxpCapability } from '../Constants/DxpCapabilities/defs/DxpCapability.defs';

/**
 * The definition of state-controlled settings for the DXP Frame.
 */
export interface DxpFrameStore {
  /**
   * The DXP capability the user is within.
   */
  capability: DxpCapability | null;

  /**
   * The readable state value of the side navigation menu sections.
   */
  navigationSections: Array<NavigationSection>;

  /**
   * The readable state value for showing the frame header.
   */
  showHeader: boolean;

  /**
   * The readable state value for showing the frame side navigation panel.
   */
  showSideNavigation: boolean;

  /**
   * Update the DXP capability.
   */
  updateCapability: (newValue: DxpCapability | null) => void;

  /**
   * Update the side navigation menu sections.
   */
  updateNavigationSections: (newValue: Array<NavigationSection>) => void;

  /**
   * Toggle inclusion of the frame header.
   */
  updateShowHeader: (newValue: boolean) => void;

  /**
   * Toggle inclusion of the frame side navigation panel.
   */
  updateShowSideNavigation: (newValue: boolean) => void;
}

/**
 * The state store for the DXP Frame.
 */
export const useDxpFrameStore = create<DxpFrameStore>()((set) => ({
  capability: null,
  navigationSections: [],
  showHeader: true,
  showSideNavigation: true,
  updateCapability: (newValue: DxpCapability | null): void => {
    set({ capability: newValue });
  },
  updateNavigationSections: (newValue: Array<NavigationSection>): void => {
    set({ navigationSections: newValue });
  },
  updateShowHeader: (newValue: boolean): void => {
    set({ showHeader: newValue });
  },
  updateShowSideNavigation: (newValue: boolean): void => {
    set({ showSideNavigation: newValue });
  },
}));
